/**
 * Imports
 */
import {
  deleteAtPath,
  deleteFolder,
  resetOwnerFiles,
  resolveDownloadUrl,
  updateOwnerFile,
  uploadToPath,
} from 'firebaseAPI';
import * as React from 'react';
import { Firebase } from 'types/firebase';
import { getFileExtension } from 'utils/content-types';
/**
 * Types
 */
export type FilesApiAPIHandlers = {
  onDownloadImage: (fileUrl: string) => Promise<void>;
  onDeleteAllImages: (path: string, merchantId: string) => Promise<void>;
  onDeleteImage: (
    path: string,
    mid: string,
    fileIndex: number,
    oid: number,
  ) => Promise<void>;
  onUploadImage: (data: {
    file: File;
    storagePath: string;
    photoType: number;
    merchantId: string;
    ownerIndex: number;
  }) => Promise<void>;
};
export type FileUrlObj = {
  downloadUrl: string;
  storagePath: string;
  fileName: string;
  fileIndex: number;
};
export type FilesApiAPIType = {
  handlers: FilesApiAPIHandlers;
  isLoading: boolean;
  fileUrls: FileUrlObj[];
};
export interface FilesApiProps {
  owner?: Firebase.Owner;
}

/**
 * CONST
 */
const ID_ROOT_FOLDER = 'onboarding_photo_ids';

/**
 * useFilesApi
 */
const useFilesApi = ({ owner }: FilesApiProps): FilesApiAPIType => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [fileUrls, setFileUrls] = React.useState<FileUrlObj[]>([]);

  /**
   * The bucket recorded for one slot, mirroring the new dashboard's
   * `collectOwnerImageRefs`: the per-slot field wins, then the owner-wide legacy one.
   * Documents in the `fileUrl` map have no per-slot field, so they get the owner-wide
   * value and otherwise rely on probing.
   */
  const _photoIdBucket = () => owner?.photoIdBucket || owner?.bucketName;
  const _addressIdBucket = () => owner?.addressIdBucket || owner?.bucketName;

  const _findUrl = async (fileUrl: string) => resolveDownloadUrl(fileUrl);

  /**
   * Download image
   * @param - fileUrl a relative path on firebase storage
   */
  const _downloadImageFromPath = async (fileUrl: string): Promise<any> => {
    _findUrl(fileUrl)
      .then((url) => {
        const splittedFiledName = fileUrl.split('/');
        const fileName = `${splittedFiledName[1]}_${splittedFiledName[2]}`;

        const xhr = new XMLHttpRequest();
        xhr.responseType = 'blob';
        xhr.onload = function () {
          const blob = xhr.response;
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = fileName;
          link.click();
          URL.revokeObjectURL(link.href);
        };
        xhr.open('GET', url);
        xhr.send();
      })
      .catch((error) => {
        console.log('Error while downloading image', error);
      });
  };

  /**
   * Upload file
   */
  const _uploadImage = async (file: File, storagePath: string) => {
    const fileType = getFileExtension(file.type);
    const storagePathWithExt = `${storagePath.replace(
      /.jpeg|.png|.pdf/gi,
      '',
    )}.${fileType}`;
    const metadata = {
      cacheControl: 'public, max-age=900',
      contentType: file.type,
    };

    // Everything new goes to customer-files — never the project default, and not the
    // owner's previously recorded bucket either: where a merchant's *older* files happen
    // to sit is no reason to keep adding to that bucket, and reads probe both.
    const bucketName = await uploadToPath(storagePathWithExt, file, metadata);

    _getImages();

    // Bare bucket name, the form recorded on the owner map.
    return { path: storagePathWithExt, bucketName };
  };

  /**
   * _getImageUrl
   */
  const _getImageUrl = (
    path: string,
    recordedBucket?: string,
  ): Promise<string> => resolveDownloadUrl(path, recordedBucket);

  /**
   * GetImages
   */
  const _getImages = () => {
    if (!owner) {
      return;
    }
    const fileUrlMap = owner.fileUrl;
    // Logged whole: if `bucketName` is absent here but the file is really in
    // customer-files, that mismatch is the thing worth seeing.
    console.log('[useFilesApi] owner file fields', {
      bucketName: owner.bucketName ?? '(not set)',
      ownerMapKeys: Object.keys(owner),
      photoId: owner.photoId ?? null,
      addressId: owner.addressId ?? null,
      fileUrl: fileUrlMap ?? null,
    });
    setFileUrls([]);
    if (owner?.addressId) {
      _getImageUrl(owner.addressId, _addressIdBucket())
        .then((downloadUrl) => {
          const fileName = owner
            .addressId!.split('/')[2]
            .replace(/.jpeg|.png|.pdf/gi, '');
          return setFileUrls((prev) => [
            ...prev,
            {
              downloadUrl,
              storagePath: owner.addressId!,
              fileName,
              fileIndex: 1,
            },
          ]);
        })
        // Without this the row just silently never appears, which is what made the
        // original bug look like "there is no file" rather than "we looked in the
        // wrong bucket".
        .catch((error) => {
          console.error(
            '[useFilesApi] addressId unavailable in every bucket',
            owner.addressId,
            error,
          );
        });
    }
    if (owner?.photoId) {
      _getImageUrl(owner.photoId, _photoIdBucket())
        .then((downloadUrl) => {
          const fileName = owner
            .photoId!.split('/')[2]
            .replace(/.jpeg|.png|.pdf/gi, '');
          return setFileUrls((prev) => [
            ...prev,
            {
              downloadUrl,
              storagePath: owner.photoId!,
              fileName,
              fileIndex: 0,
            },
          ]);
        })
        .catch((error) => {
          console.error(
            '[useFilesApi] photoId unavailable in every bucket',
            owner.photoId,
            error,
          );
        });
    }
    if (fileUrlMap != null) {
      Object.values(fileUrlMap).map((storagePath, fileIndex) => {
        // Documents have no per-slot bucket field; the owner-wide one is the best hint,
        // and probing covers the rest.
        _getImageUrl(storagePath, owner.bucketName)
          .then((downloadUrl) => {
            const fileName = storagePath
              .split('/')[2]
              .replace(/.jpeg|.png|.pdf/gi, '');
            return setFileUrls((prev) => [
              ...prev,
              { downloadUrl, storagePath, fileName, fileIndex },
            ]);
          })
          .catch((error) => {
            console.error(
              '[useFilesApi] document unavailable in every bucket',
              storagePath,
              error,
            );
          });
      });
    }
  };

  React.useEffect(() => {
    if (owner) {
      _getImages();
    }
  }, [owner, owner?.fileUrl]);

  const handlers: FilesApiAPIHandlers = {
    /**
     * onFilesApi
     */
    onDownloadImage: async (fileUrl) => {
      await _downloadImageFromPath(fileUrl);
    },
    /**
     * onDeleteAllImages
     */
    onDeleteAllImages: async (path: string, merchantId: string) => {
      // Awaited, and across both buckets: this is the GDPR purge, so it has to finish
      // before the caller moves on, and the ID files it is clearing sit in customer-files
      // for anything the new dashboard touched.
      const { failed, unreadableBuckets } = await deleteFolder(
        `${ID_ROOT_FOLDER}/${path}`,
      );
      if (failed > 0 || unreadableBuckets > 0) {
        alert(
          `KYC image purge incomplete: ${failed} file(s) could not be deleted and ${unreadableBuckets} bucket(s) could not be listed — check the console and remove them manually.`,
        );
      }

      await resetOwnerFiles(merchantId);
    },
    /**
     * onDeleteImage
     */
    onDeleteImage: async (
      path: string,
      merchantId: string,
      photoType: number,
      ownerIndex: number,
    ) => {
      setIsLoading(true);
      await deleteAtPath(path);
      await updateOwnerFile(merchantId, photoType, null, ownerIndex);
      setIsLoading(false);
    },
    onUploadImage: async (data) => {
      setIsLoading(true);
      const { file, storagePath, photoType, merchantId, ownerIndex } = data;
      const { path, bucketName } = await _uploadImage(file, storagePath);
      // Record where it went, in the same write as the path — so the next read (from
      // either dashboard) knows the bucket instead of having to discover it by probing.
      await updateOwnerFile(
        merchantId,
        photoType,
        path,
        ownerIndex,
        bucketName,
      );
      _getImages();
      setIsLoading(false);
    },
  };
  return { handlers, isLoading, fileUrls };
};

/**
 * Exports
 */
export { useFilesApi };
